import React from 'react'
import { enableMapSet, produce } from 'immer'
import { createStore } from 'redux'
import { Provider, useDispatch, useSelector, useStore, shallowEqual } from 'react-redux'
import { FILE_STATE } from './types'
import './types'

enableMapSet()

/** アクションタイプ定数（他モジュールからの参照用） */
export const ACTION_TYPES = {
  RESET: 'RESET',
  SET_PAGE_STATE: 'SET_PAGE_STATE',
  SET_TASK_ID: 'SET_TASK_ID',
  SET_DATA_CREATE_TASK: 'SET_DATA_CREATE_TASK',
  SET_FILE_CLASSIFICATION_LIST: 'SET_FILE_CLASSIFICATION_LIST',
  SET_SAVED_UPLOAD_STATE: 'SET_SAVED_UPLOAD_STATE',
  ADD_FILE: 'ADD_FILE',
  INIT_FILE_DATA: 'INIT_FILE_DATA',
  UPDATE_FILE: 'UPDATE_FILE',
  SYNC_FILE_META: 'SYNC_FILE_META',
  DELETE_FILE: 'DELETE_FILE',
  RESTORE_FILE: 'RESTORE_FILE',
  REMOVE_FILE: 'REMOVE_FILE',
  TOGGLE_EXPANDED_FILE: 'TOGGLE_EXPANDED_FILE',
  RESET_CHANGED_FLAGS: 'RESET_CHANGED_FLAGS',
  SET_ERROR_MESSAGES: 'SET_ERROR_MESSAGES',
  SET_USED_MAPPING_FILE_TYPES: 'SET_USED_MAPPING_FILE_TYPES'
}

/**
 * FileManagement の初期状態を生成する（storeを共有するため毎回新規生成） hasUnsavedChanges は fileList から useMemo で導出するため store には持たない。
 *
 * @returns {import('./types').FileManagementState}
 */
export const createFileManagementInitialState = () => ({
  pageState: 'idle',
  taskId: null,
  dataCreateTask: null,
  fileClassificationList: {
    defaultTypes: [],
    userTypes: []
  },
  fileList: {}, // Record<fileKey, FileState>
  expandedFiles: new Set(),
  savedUploadState: null, // 保存時のアップロード状態（変更検知用）
  usedMappingFileTypes: [], // IF02101 used_mapping_file_types（⑦判定用）
  errorMessages: []
})

/**
 * @param {import('./types').FileManagementState | undefined} state
 * @param {{ type: string; payload: any }} action
 * @returns {import('./types').FileManagementState}
 */
export const fileManagementReducer = (state = createFileManagementInitialState(), action) => {
  const { type, payload } = action

  if (type === ACTION_TYPES.RESET) {
    return createFileManagementInitialState()
  }

  return produce(state, (draft) => {
    switch (type) {
      case ACTION_TYPES.SET_PAGE_STATE: {
        draft.pageState = payload
        break
      }

      case ACTION_TYPES.SET_TASK_ID: {
        draft.taskId = payload
        break
      }

      case ACTION_TYPES.SET_DATA_CREATE_TASK: {
        draft.dataCreateTask = payload
        break
      }

      case ACTION_TYPES.SET_FILE_CLASSIFICATION_LIST: {
        draft.fileClassificationList = payload
        break
      }

      case ACTION_TYPES.SET_SAVED_UPLOAD_STATE: {
        draft.savedUploadState = payload
        break
      }

      case ACTION_TYPES.ADD_FILE: {
        const { fileKey, fileState } = payload
        draft.fileList[fileKey] = fileState
        break
      }

      // 初期化時のデータ補完用（isChangedを立てず、保存状態も同期する）
      case ACTION_TYPES.INIT_FILE_DATA: {
        const { fileKey, updates } = payload
        if (draft.fileList[fileKey]) {
          Object.assign(draft.fileList[fileKey], updates)
        }
        if (draft.savedUploadState && draft.savedUploadState[fileKey]) {
          Object.assign(draft.savedUploadState[fileKey], updates)
        }
        break
      }

      case ACTION_TYPES.UPDATE_FILE: {
        const { fileKey, updates } = payload
        if (draft.fileList[fileKey]) {
          Object.assign(draft.fileList[fileKey], updates)
          draft.fileList[fileKey].isChanged = true
        }
        break
      }

      // ローカル保存済みメタデータ（headersなど）をstoreへ同期する用途
      case ACTION_TYPES.SYNC_FILE_META: {
        const { fileKey, updates } = payload
        if (draft.fileList[fileKey]) {
          Object.assign(draft.fileList[fileKey], updates)
        }
        break
      }

      case ACTION_TYPES.DELETE_FILE: {
        const { fileKey } = payload
        if (draft.fileList[fileKey]) {
          draft.fileList[fileKey].fileState = FILE_STATE.PENDING_DELETION
          draft.fileList[fileKey].isChanged = true
        }
        break
      }

      case ACTION_TYPES.RESTORE_FILE: {
        const { fileKey } = payload
        if (draft.fileList[fileKey]) {
          draft.fileList[fileKey].fileState = FILE_STATE.PENDING_VIRUS_CHECK
          draft.fileList[fileKey].isChanged = true
        }
        break
      }

      case ACTION_TYPES.REMOVE_FILE: {
        const { fileKey } = payload
        delete draft.fileList[fileKey]
        break
      }

      case ACTION_TYPES.TOGGLE_EXPANDED_FILE: {
        const { fileKey } = payload
        if (draft.expandedFiles.has(fileKey)) {
          draft.expandedFiles.delete(fileKey)
        } else {
          draft.expandedFiles.add(fileKey)
        }
        break
      }

      case ACTION_TYPES.RESET_CHANGED_FLAGS: {
        Object.values(draft.fileList).forEach((file) => {
          file.isChanged = false
        })
        break
      }

      case ACTION_TYPES.SET_ERROR_MESSAGES: {
        const { clear = false, messages = [] } = payload
        draft.errorMessages = clear ? messages : [...draft.errorMessages, ...messages]
        break
      }

      case ACTION_TYPES.SET_USED_MAPPING_FILE_TYPES: {
        draft.usedMappingFileTypes = Array.isArray(payload) ? payload : []
        break
      }

      default: {
        break
      }
    }
  })
}

/** @returns {function(string, any): void} */
export const useFileManagementDispatch = () => {
  const dispatch = useDispatch()
  return (type, payload) => dispatch({ type, payload })
}

/** @type {import('react-redux').TypedUseSelectorHook<import('./types').FileManagementState>} */
export const useFileManagementSelector = useSelector

/** @type {() => import('redux').Store} */
export const useFileManagementStore = useStore

export { shallowEqual }

/**
 * @param {any} props
 * @returns
 */
export const FileManagementProviderStore = (props) => {
  const [store] = React.useState(() => createStore(fileManagementReducer))
  return <Provider store={store}>{props.children}</Provider>
}
